/**
 * The open set of the shell, its row and its session state. The URL `panels=` names the open
 * panels, and a bare URL falls back to the stored record. Each write that navigates or changes
 * the focus history or the parked set is here; `shellRecordAtoms.ts` has the record-only writes.
 */

import type { Getter, Setter } from 'jotai';
import { atom } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { atomFamily } from 'jotai-family';

import { fitsBeside, selectOverlays, selectVisible, selectVisiblePhone } from '@/app/allocate';
import { getClosingPanels } from '@/app/panelSummons';
import type { PanelTarget } from '@/app/panelUrl';
import {
    buildSearchString, hydrateOpen, isForeignOrgEntry, navigatePanelUrl, parsePanelIds,
    readSharedFromUrl, sortByOrder,
} from '@/app/panelUrl';
import { panels as registry } from '@/app/registry';
import { getAutoChildId } from '@/app/resolveOpen';
import { projectViewState } from '@/app/viewState';
import { authSession } from '@/auth/authSession';
import type {
    DefiniteRequest, OpenPanel, OpenPanelOptions, OpenPlan, OpenPlanner, OpenRequest,
    PanelAdmits, PanelId, PanelMode, PersistedShared, SharedPanelProps, ShellRecord, ViewState,
} from '@/types/panels';

import { navigationAtom, shellLocationAtom } from './shellLocationAtoms';
import { planOpen } from './shellOpenPlan';
import { deriveMode, getFocusBehind, onStage } from './shellPanels';
import {
    focusedIdAtom, localPropsAtom, persistedSharedAtom, storedOpenPanelIdsAtom,
    updateShellRecordAtom, userWidthsAtom,
} from './shellRecordAtoms';
import { isSmViewportAtom, stageWidthAtom } from './shellStageAtoms';

interface CommitOptions {
    sharedOverrides?: SharedPanelProps;
    shouldReplace?: boolean;
    writeShellRecord?: () => void;
}

// The panels that gave the front up, most recent last, so a close goes back to the panel that
// was in front. Session state: after a reload, getFocusBehind uses the neighbours.
const focusHistoryStateAtom = atom<readonly PanelId[]>([]);

// The panels that × dismissed but kept open as the anchor of their mode. They take no room in
// the row, and they close with the last panel that is not parked. Session state.
const parkedStateAtom = atom<ReadonlySet<PanelId>>(new Set<PanelId>());

// Read-only: the write atoms below are the only writers.
export const focusHistoryAtom = atom((get): readonly PanelId[] => get(focusHistoryStateAtom));

export const parkedPanelIdsAtom = atom((get): ReadonlySet<PanelId> => get(parkedStateAtom));

const searchAtom = atom((get): string => get(shellLocationAtom).search);

// One identity for each search, because openPanels and the shared props depend on it.
const searchParamsAtom = atom((get): URLSearchParams => new URLSearchParams(get(searchAtom)));

// An entry written under another organization keeps its panels, not the ids in its URL.
const isForeignOrgAtom = atom((get): boolean => isForeignOrgEntry(get(shellLocationAtom).state));

// The URL `panels=` is authoritative, also for what it omits: a shared link never gets the
// stored camera. Only a bare URL reads the stored panels and the stored shared props.
export const openPanelsAtom = atom((get): OpenPanel[] => {
    const searchParams = get(searchParamsAtom);
    const urlPanelsRaw = searchParams.get('panels');
    const isFromStorage = urlPanelsRaw === null;
    const ids = isFromStorage ? get(storedOpenPanelIdsAtom) : parsePanelIds(urlPanelsRaw);

    const sharedFromUrl = get(isForeignOrgAtom) ? {} : readSharedFromUrl(searchParams);
    const localPropsByPanel = get(localPropsAtom);
    const fallbackShared: PersistedShared = isFromStorage ? get(persistedSharedAtom) : {};
    const result: OpenPanel[] = [];
    for (const id of ids) {
        const entry = registry[id];
        if (!entry) {
            continue;
        }
        const localProps = localPropsByPanel[id] ?? {};
        const overlay: Record<string, unknown> = {};
        for (const key of entry.config.urlSyncedProps ?? []) {
            const value = sharedFromUrl[key] ?? fallbackShared[key];
            if (value !== undefined) {
                overlay[key] = value;
            }
        }
        result.push({ id, config: entry.config, props: { ...localProps, ...overlay } });
    }
    return sortByOrder(result);
});

// Derived from the open set, so that the mode and the open panels come from one navigation.
export const modeAtom = atom((get): PanelMode => deriveMode(get(openPanelsAtom)));

/** The open panels that take room in the row: all but the chat drawer, off a phone. */
export const stagePanelsAtom = atom((get): OpenPanel[] => {
    const isSmViewport = get(isSmViewportAtom);
    return get(openPanelsAtom).filter((panel) => onStage(panel, isSmViewport));
});

const rowPanelsAtom = atom((get): OpenPanel[] => {
    const parkedPanelIds = get(parkedStateAtom);
    return get(stagePanelsAtom).filter((panel) => !parkedPanelIds.has(panel.id));
});

// The run of panels that fits and keeps the anchor (selectVisible). A phone shows the anchor.
function selectRow(get: Getter, panels: OpenPanel[], anchor: PanelId | null): OpenPanel[] {
    if (get(isSmViewportAtom)) {
        return selectVisiblePhone({ openPanels: panels, focusedId: anchor });
    }
    return selectVisible({
        openPanels: panels, stageWidth: get(stageWidthAtom), userWidths: get(userWidthsAtom),
        focusedId: anchor,
    });
}

/** The panels the row has room for, anchored on the focused one. */
export const visiblePanelsAtom = atom(
    (get): OpenPanel[] => selectRow(get, get(rowPanelsAtom), get(focusedIdAtom)));

/** The overlay panels the row has no room for, floated over it. */
export const overlayPanelsAtom = atom((get): OpenPanel[] => {
    if (get(isSmViewportAtom)) {
        return [];
    }
    return selectOverlays({
        openPanels: get(rowPanelsAtom), visiblePanels: get(visiblePanelsAtom),
    });
});

function getPanelIds(panels: OpenPanel[]): ReadonlySet<PanelId> {
    return new Set(panels.map((panel) => panel.id));
}

function hasSameMembers(left: ReadonlySet<PanelId>, right: ReadonlySet<PanelId>): boolean {
    return left.size === right.size && [...left].every((id) => right.has(id));
}

// One identity while the members stay, so the rail does not render on each frame of a slide.
export const visibleIdsAtom = selectAtom(visiblePanelsAtom, getPanelIds, hasSameMembers);

// The rail shows the workflow of the operator, not only the panels that fit. One identity
// while the members stay.
export const openIdsAtom = selectAtom(stagePanelsAtom, getPanelIds, hasSameMembers);

// An automatic open adds only a panel that the row can show beside the others, because the user
// would find a hidden one after a close. A phone shows one panel, so it admits no second one.
export const admitsBesideAtom = atom((get): ((anchor: PanelId | null) => PanelAdmits) => {
    const isSmViewport = get(isSmViewportAtom);
    const stageWidth = get(stageWidthAtom);
    const userWidths = get(userWidthsAtom);
    const parkedPanelIds = get(parkedStateAtom);
    return (anchor: PanelId | null): PanelAdmits => (
        (panels: OpenPanel[], addition: OpenPanel): boolean => !isSmViewport && fitsBeside({
            openPanels: panels.filter((panel) => (
                onStage(panel, isSmViewport) && !parkedPanelIds.has(panel.id)
            )),
            candidate: addition,
            stageWidth,
            userWidths,
            focusedId: anchor,
        })
    );
});

/** The outcome of a request on the open set as the store is now: see shellOpenPlan.ts. */
export const openPlannerAtom = atom((get): OpenPlanner => {
    const openPanels = get(openPanelsAtom);
    const mode = get(modeAtom);
    const isSmViewport = get(isSmViewportAtom);
    const focusedId = get(focusedIdAtom);
    const localPropsByPanel = get(localPropsAtom);
    const admits = get(admitsBesideAtom);
    function plan(request: DefiniteRequest): OpenPlan;
    function plan(request: OpenRequest): OpenPlan | null;
    function plan(request: OpenRequest): OpenPlan | null {
        return planOpen({
            openPanels, mode, isSmViewport, focusedId, localPropsByPanel, admits, registry,
            request,
        });
    }
    return plan;
});

/**
 * The list whose auto child `id` is, when a close of `id` leaves the mode and shows that list
 * again. Then the close keeps the workflow of the list. Null when a close is only a close.
 */
export const panelRevealedByClosingAtom = atomFamily((id: PanelId) => atom(
    (get): PanelId | null => {
        const visiblePanelIds = get(visibleIdsAtom);
        if (!visiblePanelIds.has(id)) {
            return null;
        }
        const openPanels = get(openPanelsAtom);
        const mode = get(modeAtom);
        const closing = getClosingPanels(openPanels, id);
        const remainingOpenPanels = openPanels.filter((panel) => !closing.has(panel.id));
        if (deriveMode(remainingOpenPanels) === mode) {
            return null;
        }
        const front = get(focusedIdAtom);
        const anchor = front !== null && closing.has(front)
            ? getFocusBehind(
                get(rowPanelsAtom).map((panel) => panel.id), get(focusHistoryStateAtom), front,
                closing)
            : front;
        if (anchor === null || visiblePanelIds.has(anchor) ||
            getAutoChildId(registry[anchor]?.config, mode) !== id) {
            return null;
        }
        const isSmViewport = get(isSmViewportAtom);
        const parkedPanelIds = get(parkedStateAtom);
        const row = remainingOpenPanels.filter((panel) => onStage(panel, isSmViewport) &&
            !parkedPanelIds.has(panel.id));
        return selectRow(get, row, anchor).some((panel) => panel.id === anchor) ? anchor : null;
    }));

// The shell can mount again on the same store, after a page outside it, and the session state
// belongs to one mount.
export const clearShellSessionAtom = atom(null, (_get, set): void => {
    set(focusHistoryStateAtom, (history) => (history.length === 0 ? history : []));
    set(parkedStateAtom, (parked) => (parked.size === 0 ? parked : new Set<PanelId>()));
});

// Call it outside a record updater, because an updater must be pure.
function pushFocusHistory(
    set: Setter, leavingPanelId: PanelId | null, arriving: PanelId | null,
): void {
    if (leavingPanelId === null || leavingPanelId === arriving) {
        return;
    }
    set(focusHistoryStateAtom, (history) => [
        ...history.filter((id) => id !== leavingPanelId), leavingPanelId,
    ]);
}

function unpark(set: Setter, id: PanelId | null): void {
    set(parkedStateAtom, (parked) => (
        id !== null && parked.has(id) ? new Set([...parked].filter((pid) => pid !== id)) : parked
    ));
}

// Built at flush time from the current location, so that the commits of one tick compose.
// Null `panelIds` keeps the panels that the URL has by then.
function getPanelUrlTarget(
    get: Getter, panelIds: string[] | null, sharedOverrides: SharedPanelProps,
): () => PanelTarget {
    const openPanelIds = get(openPanelsAtom).map((panel) => panel.id);
    return (): PanelTarget => {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get('panels');
        const ids = panelIds ?? (fromUrl !== null ? parsePanelIds(fromUrl) : openPanelIds);
        const sharedPanelProps = readSharedFromUrl(params);
        for (const [key, value] of Object.entries(sharedOverrides)) {
            if (value === null || value === undefined) {
                delete sharedPanelProps[key];
            } else {
                sharedPanelProps[key] = String(value);
            }
        }
        const search = buildSearchString(ids, sharedPanelProps);
        return { pathname: '/', search: `?${search}` };
    };
}

// Flush-synchronous, because the URL gives the rendered panels. `writeShellRecord` is the
// stored part of the same write.
function commitUrl(get: Getter, panels: OpenPanel[], opts: CommitOptions = {}): void {
    const ids = sortByOrder(panels).map((panel) => panel.id);
    navigatePanelUrl(
        get(navigationAtom).navigate, getPanelUrlTarget(get, ids, opts.sharedOverrides ?? {}),
        opts.shouldReplace, opts.writeShellRecord);
}

// Shared props alone, leaving the open-panel list to the URL.
function commitSharedProps(
    get: Getter, overrides: SharedPanelProps, writeShellRecord?: () => void,
): void {
    navigatePanelUrl(
        get(navigationAtom).navigate, getPanelUrlTarget(get, null, overrides), false,
        writeShellRecord);
}

// Normalize external links and lift the saved workspace into a bare URL. An explicit
// `panels=` keeps an empty workspace authoritative on Back, reload and shared links.
export const normalizeShellUrlAtom = atom(null, (get): void => {
    const location = get(shellLocationAtom);
    const searchParams = new URLSearchParams(location.search);
    const needsPanels = !searchParams.has('panels');
    const ids = needsPanels
        ? sortByOrder(hydrateOpen(get(storedOpenPanelIdsAtom), get(localPropsAtom)))
            .map((panel) => panel.id)
        : parsePanelIds(searchParams.get('panels'));
    const urlShared = isForeignOrgEntry(location.state) ? {} : readSharedFromUrl(searchParams);
    const search = buildSearchString(
        ids, needsPanels ? { ...get(persistedSharedAtom), ...urlShared } : urlShared);
    get(navigationAtom).navigate(
        { pathname: '/', search: `?${search}` },
        { replace: true, state: { orgId: authSession.getActiveOrg() } });
});

// The auto child for the panels that a mount restores, because no openPanel resolved them.
export const openRestoredAutoChildAtom = atom(null, (get, set): void => {
    const plan = get(openPlannerAtom)({ kind: 'autoChild' });
    if (plan === null) {
        return;
    }
    const { nextPanels } = plan;
    commitUrl(get, nextPanels, {
        writeShellRecord: (): void => {
            set(updateShellRecordAtom, (record): ShellRecord => ({
                ...record, openPanels: nextPanels.map((panel) => panel.id),
            }));
        },
    });
});

// Gives the view state after the open, so that a caller (the open_panel tool of the chat) sees
// whether the panel opened.
export const openPanelAtom = atom(null, (
    get, set, id: PanelId, props?: Record<string, unknown>, options?: OpenPanelOptions,
): ViewState => {
    const focusedId = get(focusedIdAtom);
    const { nextPanels, focusId, urlProps, localProps, shouldReplace } =
        get(openPlannerAtom)({ kind: 'open', id, props, options });
    pushFocusHistory(set, focusedId, focusId);
    unpark(set, focusId);
    const nextPanelIds = getPanelIds(nextPanels);

    commitUrl(get, nextPanels, {
        sharedOverrides: urlProps,
        shouldReplace,
        writeShellRecord: (): void => {
            set(parkedStateAtom, (parked) => (
                [...parked].every((pid) => nextPanelIds.has(pid))
                    ? parked
                    : new Set([...parked].filter((pid) => nextPanelIds.has(pid)))
            ));
            set(updateShellRecordAtom, (record): ShellRecord => {
                const nextLocalProps = { ...record.scratch };
                if (Object.keys(localProps).length > 0) {
                    nextLocalProps[id] = { ...(nextLocalProps[id] ?? {}), ...localProps };
                }
                return {
                    ...record,
                    openPanels: nextPanels.map((panel) => panel.id),
                    scratch: nextLocalProps,
                    focusedId: focusId,
                };
            });
        },
    });

    return projectViewState({ openPanels: nextPanels, focusedId: focusId });
});

// Closes `closing` in one commit. A prop write gives its `opts`, because the close of its
// dependent panels is part of its intent.
const closePanelsAtom = atom(null, (
    get, set, closing: ReadonlySet<PanelId>, opts: CommitOptions = {},
): void => {
    const storedOpenPanelIds = get(storedOpenPanelIdsAtom);
    const isSmViewport = get(isSmViewportAtom);
    let remainingIds = new Set(storedOpenPanelIds.filter((pid) => !closing.has(pid)));
    const stillParked = new Set([...get(parkedStateAtom)].filter((pid) => remainingIds.has(pid)));
    const hasRemainingOnStage = get(openPanelsAtom).some((panel) => (
        remainingIds.has(panel.id) && onStage(panel, isSmViewport) && !stillParked.has(panel.id)
    ));
    if (!hasRemainingOnStage) {
        remainingIds = new Set([...remainingIds].filter((pid) => !stillParked.has(pid)));
        stillParked.clear();
    }
    const nextIds = storedOpenPanelIds.filter((pid) => remainingIds.has(pid));

    const focusedId = get(focusedIdAtom);
    const focusHistory = get(focusHistoryStateAtom);
    const isFrontClosing = focusedId !== null && closing.has(focusedId);
    const nextFocusedId = isFrontClosing
        ? getFocusBehind(
            get(rowPanelsAtom).map((panel) => panel.id), focusHistory, focusedId, closing)
        : focusedId;

    commitUrl(get, hydrateOpen(nextIds, get(localPropsAtom)), {
        ...opts,
        writeShellRecord: (): void => {
            set(parkedStateAtom, stillParked);
            const isHistoryUnchangedSinceCall = get(focusHistoryStateAtom) === focusHistory;
            if (isFrontClosing && isHistoryUnchangedSinceCall) {
                set(focusHistoryStateAtom, (history) => history.slice(0, -1));
            }
            set(focusHistoryStateAtom, (history) => (
                history.filter((pid) => remainingIds.has(pid))
            ));
            set(updateShellRecordAtom, (record): ShellRecord => ({
                ...record,
                openPanels: nextIds,
                focusedId: record.focusedId === focusedId ? nextFocusedId : record.focusedId,
            }));
            opts.writeShellRecord?.();
        },
    });
});

export const closePanelAtom = atom(null, (get, set, id: PanelId): void => {
    set(closePanelsAtom, getClosingPanels(get(openPanelsAtom), id));
});

/** Closes every panel on the stage. The chat drawer stays. */
export const closeStagePanelsAtom = atom(null, (get, set): void => {
    const stagePanels = get(stagePanelsAtom);
    const nextIds = get(storedOpenPanelIdsAtom).filter(
        (pid) => !stagePanels.some((panel) => panel.id === pid));
    commitUrl(get, hydrateOpen(nextIds, get(localPropsAtom)), {
        writeShellRecord: (): void => {
            set(parkedStateAtom, new Set<PanelId>());
            set(focusHistoryStateAtom, []);
            set(updateShellRecordAtom, (record): ShellRecord => ({
                ...record, openPanels: nextIds, focusedId: null,
            }));
        },
    });
});

// Brings an open panel forward without a new open: on a narrow stage, the focus decides the row.
export const focusPanelAtom = atom(null, (get, set, id: PanelId): void => {
    const focusedId = get(focusedIdAtom);
    if (focusedId === id || !get(stagePanelsAtom).some((panel) => panel.id === id)) {
        return;
    }
    pushFocusHistory(set, focusedId, id);
    unpark(set, id);
    set(updateShellRecordAtom, (record): ShellRecord => (
        record.focusedId === id ? record : { ...record, focusedId: id }
    ));
});

/** Dismiss `from` without closing it and bring `to` forward — the × as Back. */
export const stepBackAtom = atom(null, (_get, set, from: PanelId, to: PanelId): void => {
    set(parkedStateAtom, (parked) => new Set([...parked, from]));
    set(focusPanelAtom, to);
});

// Several props of one panel in one write, because a camera and its moment are one intent.
export const setPanelPropsAtom = atom(null, (
    get, set, id: PanelId, props: Record<string, unknown>,
): void => {
    const { nextPanels, urlProps, localProps, shouldReplace } =
        get(openPlannerAtom)({ kind: 'props', id, props });
    const writeLocalProps = Object.keys(localProps).length === 0
        ? undefined
        : (): void => set(updateShellRecordAtom, (record): ShellRecord => {
            const nextEntry: Record<string, unknown> = { ...(record.scratch[id] ?? {}) };
            for (const [key, value] of Object.entries(localProps)) {
                if (value === null || value === undefined) {
                    delete nextEntry[key];
                } else {
                    nextEntry[key] = value;
                }
            }
            return { ...record, scratch: { ...record.scratch, [id]: nextEntry } };
        });
    const nextPanelIds = getPanelIds(nextPanels);
    const closingIds = new Set(get(openPanelsAtom)
        .filter((panel) => !nextPanelIds.has(panel.id))
        .map((panel) => panel.id));
    if (closingIds.size > 0) {
        set(closePanelsAtom, closingIds, {
            sharedOverrides: urlProps, shouldReplace, writeShellRecord: writeLocalProps,
        });
    } else if (Object.keys(urlProps).length > 0) {
        commitSharedProps(get, urlProps, writeLocalProps);
    } else {
        writeLocalProps?.();
    }
});

export const setPanelPropAtom = atom(
    null, (_get, set, id: PanelId, key: string, value: unknown): void => {
        set(setPanelPropsAtom, id, { [key]: value });
    });
